import { IProduct, IVariant, IHandlingUnit } from '../models/products.models';
import { ICatalog } from '../../features/catalogs/models/catalogs.models';

type CatalogType = ICatalog | Partial<ICatalog> | null | undefined;

/**
 * Helper pour gérer les handling units de manière cohérente
 */
export class HandlingUnitsHelper {
  /**
   * Calcule le nombre total d'unités à partir de qte_selected (unités totales) et du handling unit sélectionné
   * Note: qte_selected est toujours en unités totales (total units)
   */
  static getTotalUnits(product: IProduct | IVariant): number {
    return product.qte_selected || 0;
  }

  /**
   * Calcule le nombre d'unités de handling unit à partir de qte_selected (unités totales) et du handling unit sélectionné
   */
  static getHandlingUnitQuantity(product: IProduct | IVariant, catalog?: CatalogType): number {
    const qteTotal = product.qte_selected || 0;
    const currentHUId = this.getCurrentHandlingUnitId(product, catalog);
    
    if (!currentHUId || !product.handling_units?.length) {
      return qteTotal;
    }
    
    const selectedHU = product.handling_units.find(hu => hu.id === currentHUId);
    
    if (!selectedHU || !selectedHU.quantity || selectedHU.quantity <= 0) {
      return qteTotal;
    }
    
    return Math.floor(qteTotal / selectedHU.quantity);
  }

  /**
   * Obtient l'ID du handling unit actuellement sélectionné
   */
  static getCurrentHandlingUnitId(product: IProduct | IVariant, catalog?: CatalogType): number | null {
    if (product.selected_handling_unit_id) {
      return product.selected_handling_unit_id;
    }
    
    if (product.handling_units && product.handling_units.length > 0) {
      const defaultHU = product.handling_units.find(hu => hu.default === true);
      if (defaultHU) {
        return defaultHU.id;
      }
      
      const firstEnabled = product.handling_units.find(hu => hu.enabled !== false);
      if (firstEnabled) {
        return firstEnabled.id;
      }
    }
    
    return null;
  }

  /**
   * Obtient le handling unit sélectionné
   */
  static getSelectedHandlingUnit(product: IProduct | IVariant, catalog?: CatalogType): IHandlingUnit | null {
    const currentHUId = this.getCurrentHandlingUnitId(product, catalog);
    if (!currentHUId || !product.handling_units) {
      return null;
    }
    
    return product.handling_units.find((hu) => hu.id === currentHUId) || null;
  }

  /**
   * Vérifie si le handling unit sélectionné est différent du défaut
   */
  static isDifferentFromDefault(product: IProduct | IVariant, catalog?: CatalogType): boolean {
    const currentHUId = this.getCurrentHandlingUnitId(product, catalog);
    if (!currentHUId || !product.handling_units) {
      return false;
    }

    const selectedHU = this.getSelectedHandlingUnit(product, catalog);
    if (!selectedHU) {
      return false;
    }

    if (catalog?.default_handling_unit?.id) {
      const catalogDefaultTypeId = typeof catalog.default_handling_unit.id === 'number' 
        ? catalog.default_handling_unit.id 
        : +catalog.default_handling_unit.id;
      
      return selectedHU.type_id !== catalogDefaultTypeId;
    }

    const defaultHU = product.handling_units.find(hu => hu.default === true);
    if (defaultHU) {
      return currentHUId !== defaultHU.id;
    }

    return false;
  }

  /**
   * Calcule la quantité totale d'unités à partir de la quantité de handling unit et du handling unit sélectionné
   */
  static calculateTotalUnitsFromHUQuantity(huQuantity: number, product: IProduct | IVariant, catalog?: CatalogType): number {
    const currentHUId = this.getCurrentHandlingUnitId(product, catalog);
    
    if (!currentHUId || !product.handling_units?.length) {
      return huQuantity;
    }
    
    const selectedHU = product.handling_units.find(hu => hu.id === currentHUId);
    
    if (!selectedHU || !selectedHU.quantity || selectedHU.quantity <= 0) {
      return huQuantity;
    }
    
    return huQuantity * selectedHU.quantity;
  }

  /**
   * Calcule la quantité de handling unit à partir de la quantité totale d'unités et du handling unit sélectionné
   */
  static calculateHUQuantityFromTotalUnits(totalUnits: number, product: IProduct | IVariant, catalog?: CatalogType): number {
    const currentHUId = this.getCurrentHandlingUnitId(product, catalog);
    
    if (!currentHUId || !product.handling_units?.length) {
      return totalUnits;
    }
    
    const selectedHU = product.handling_units.find(hu => hu.id === currentHUId);
    
    if (!selectedHU || !selectedHU.quantity || selectedHU.quantity <= 0) {
      return totalUnits;
    }
    
    return Math.floor(totalUnits / selectedHU.quantity);
  }
}


